import PropTypes from 'prop-types';
import React from 'react';
import * as styles from '../../../styles/shared/inline-script.module.scss';
import { isTrustedScriptSrc } from './ck-utils';

const escapeAttr = (value) => String(value).replace(/"/g, '&quot;');

const buildSrcDoc = (attribs) => {
  const attrs = Object.entries(attribs || {})
    .map(([key, value]) => (value === '' ? key : `${key}="${escapeAttr(value)}"`))
    .join(' ');
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;padding:0;}img,iframe{max-width:100%;}</style></head><body><script ${attrs}></script></body></html>`;
};

const InlineScript = ({ attribs }) => {
  const iframeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [height, setHeight] = React.useState(0);
  const trusted = isTrustedScriptSrc(attribs?.src);

  const srcDoc = React.useMemo(() => buildSrcDoc(attribs), [attribs]);

  // Scripts de confiance (ex: Cookiebot) : executes dans le vrai document, a leur place
  // dans le contenu, car ils doivent pouvoir cibler un element du DOM de la page reelle.
  React.useEffect(() => {
    if (!trusted) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const script = document.createElement('script');
    Object.entries(attribs || {}).forEach(([key, value]) => {
      if (key === 'async') {
        script.async = true;
      } else {
        script.setAttribute(key, value);
      }
    });
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [trusted, attribs]);

  React.useEffect(() => {
    if (trusted) return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let observer;
    let pollCount = 0;

    // L'iframe srcdoc hérite de l'origine parente : on lit sa hauteur réelle pour
    // dimensionner le conteneur au contenu (formulaire qui grandit, etc.).
    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        // body.scrollHeight uniquement : documentElement.scrollHeight reflète la
        // hauteur du viewport de l'iframe et provoquerait une boucle d'agrandissement.
        const next = doc.body.scrollHeight;
        if (next) setHeight((prev) => (next !== prev ? next : prev));
      } catch {
        // contenu cross-origin (ne devrait pas arriver avec srcdoc) : on laisse tomber
      }
    };

    const onLoad = () => {
      resize();
      try {
        const doc = iframe.contentDocument;
        if (doc?.body && typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(resize);
          observer.observe(doc.body);
        }
      } catch {
        // ignore
      }
    };

    iframe.addEventListener('load', onLoad);
    // Repli pour les widgets chargés en asynchrone avant l'attache du ResizeObserver.
    const interval = setInterval(() => {
      resize();
      pollCount += 1;
      if (pollCount >= 30) clearInterval(interval);
    }, 500);

    return () => {
      iframe.removeEventListener('load', onLoad);
      observer?.disconnect();
      clearInterval(interval);
    };
  }, [trusted]);

  // Scripts inline (helper gatsby-plugin-image, etc.) : aucun rendu, comme avant.
  if (!attribs?.src) return null;

  if (trusted) {
    return <div data-embed-trusted="" ref={containerRef} />;
  }

  return (
    <iframe
      className={styles.frame}
      data-embed-frame=""
      height={height}
      ref={iframeRef}
      srcDoc={srcDoc}
      title="Contenu embarqué"
      width="100%"
    />
  );
};

InlineScript.propTypes = {
  attribs: PropTypes.object.isRequired,
};

export default InlineScript;
